import { useEffect, useState, useRef } from 'react';
import { CreateFormState } from './create.types';
import { DateTimePicker } from './DateTimePicker';
import { EventImageCredit } from '../events/EventImageCredit';

const TIMEZONES = [
  // North America
  { value: 'America/New_York', label: 'New York (EST/EDT)' },
  { value: 'America/Chicago', label: 'Chicago (CST/CDT)' },
  { value: 'America/Denver', label: 'Denver (MST/MDT)' },
  { value: 'America/Los_Angeles', label: 'Los Angeles (PST/PDT)' },
  { value: 'America/Phoenix', label: 'Phoenix (MST)' },
  { value: 'America/Anchorage', label: 'Anchorage (AKST/AKDT)' },
  { value: 'Pacific/Honolulu', label: 'Honolulu (HST)' },
  { value: 'America/Toronto', label: 'Toronto (EST/EDT)' },
  { value: 'America/Vancouver', label: 'Vancouver (PST/PDT)' },
  { value: 'America/Mexico_City', label: 'Mexico City (CST/CDT)' },
  // South America
  { value: 'America/Sao_Paulo', label: 'São Paulo (BRT)' },
  { value: 'America/Buenos_Aires', label: 'Buenos Aires (ART)' },
  { value: 'America/Bogota', label: 'Bogotá (COT)' },
  { value: 'America/Lima', label: 'Lima (PET)' },
  { value: 'America/Santiago', label: 'Santiago (CLT/CLST)' },
  // Europe
  { value: 'Europe/London', label: 'London (GMT/BST)' },
  { value: 'Europe/Paris', label: 'Paris (CET/CEST)' },
  { value: 'Europe/Berlin', label: 'Berlin (CET/CEST)' },
  { value: 'Europe/Rome', label: 'Rome (CET/CEST)' },
  { value: 'Europe/Madrid', label: 'Madrid (CET/CEST)' },
  { value: 'Europe/Amsterdam', label: 'Amsterdam (CET/CEST)' },
  { value: 'Europe/Brussels', label: 'Brussels (CET/CEST)' },
  { value: 'Europe/Vienna', label: 'Vienna (CET/CEST)' },
  { value: 'Europe/Stockholm', label: 'Stockholm (CET/CEST)' },
  { value: 'Europe/Oslo', label: 'Oslo (CET/CEST)' },
  { value: 'Europe/Copenhagen', label: 'Copenhagen (CET/CEST)' },
  { value: 'Europe/Helsinki', label: 'Helsinki (EET/EEST)' },
  { value: 'Europe/Athens', label: 'Athens (EET/EEST)' },
  { value: 'Europe/Warsaw', label: 'Warsaw (CET/CEST)' },
  { value: 'Europe/Prague', label: 'Prague (CET/CEST)' },
  { value: 'Europe/Zurich', label: 'Zurich (CET/CEST)' },
  { value: 'Europe/Dublin', label: 'Dublin (GMT/IST)' },
  { value: 'Europe/Lisbon', label: 'Lisbon (WET/WEST)' },
  { value: 'Europe/Moscow', label: 'Moscow (MSK)' },
  { value: 'Europe/Istanbul', label: 'Istanbul (TRT)' },
  // Middle East
  { value: 'Asia/Dubai', label: 'Dubai (GST)' },
  { value: 'Asia/Jerusalem', label: 'Jerusalem (IST/IDT)' },
  { value: 'Asia/Riyadh', label: 'Riyadh (AST)' },
  { value: 'Asia/Tehran', label: 'Tehran (IRST/IRDT)' },
  // Asia
  { value: 'Asia/Tokyo', label: 'Tokyo (JST)' },
  { value: 'Asia/Shanghai', label: 'Shanghai (CST)' },
  { value: 'Asia/Hong_Kong', label: 'Hong Kong (HKT)' },
  { value: 'Asia/Singapore', label: 'Singapore (SGT)' },
  { value: 'Asia/Seoul', label: 'Seoul (KST)' },
  { value: 'Asia/Taipei', label: 'Taipei (CST)' },
  { value: 'Asia/Bangkok', label: 'Bangkok (ICT)' },
  { value: 'Asia/Jakarta', label: 'Jakarta (WIB)' },
  { value: 'Asia/Manila', label: 'Manila (PHT)' },
  { value: 'Asia/Kolkata', label: 'Mumbai/Kolkata (IST)' },
  { value: 'Asia/Karachi', label: 'Karachi (PKT)' },
  { value: 'Asia/Dhaka', label: 'Dhaka (BST)' },
  { value: 'Asia/Kuala_Lumpur', label: 'Kuala Lumpur (MYT)' },
  { value: 'Asia/Ho_Chi_Minh', label: 'Ho Chi Minh (ICT)' },
  // Oceania
  { value: 'Australia/Sydney', label: 'Sydney (AEST/AEDT)' },
  { value: 'Australia/Melbourne', label: 'Melbourne (AEST/AEDT)' },
  { value: 'Australia/Brisbane', label: 'Brisbane (AEST)' },
  { value: 'Australia/Perth', label: 'Perth (AWST)' },
  { value: 'Australia/Adelaide', label: 'Adelaide (ACST/ACDT)' },
  { value: 'Pacific/Auckland', label: 'Auckland (NZST/NZDT)' },
  { value: 'Pacific/Fiji', label: 'Fiji (FJT)' },
  // Africa
  { value: 'Africa/Cairo', label: 'Cairo (EET)' },
  { value: 'Africa/Johannesburg', label: 'Johannesburg (SAST)' },
  { value: 'Africa/Lagos', label: 'Lagos (WAT)' },
  { value: 'Africa/Nairobi', label: 'Nairobi (EAT)' },
  { value: 'Africa/Casablanca', label: 'Casablanca (WET/WEST)' },
];

const EVENT_IMAGE_MAX_BYTES = 10 * 1024 * 1024;
const EVENT_IMAGE_ACCEPT = 'image/jpeg,image/png,image/webp,image/gif,image/heic,image/heif,.heic,.heif';
const previewableImageMimeTypes = new Set(['image/jpeg', 'image/png', 'image/webp', 'image/gif']);
const allowedEventImageMimeTypes = new Set([
  ...previewableImageMimeTypes,
  'image/heic',
  'image/heif',
  'image/heic-sequence',
  'image/heif-sequence',
]);
const allowedEventImageExtensions = new Set(['.jpg', '.jpeg', '.png', '.webp', '.gif', '.heic', '.heif']);
const heicImageExtensions = new Set(['.heic', '.heif']);

interface EventDetailsStepProps {
  formState: CreateFormState;
  onUpdate: (updates: Partial<CreateFormState>) => void;
  onSubmit: () => void;
  onCancel: () => void;
  onDelete?: () => void;
  onDuplicatePastEvent?: () => void;
  isSubmitting: boolean;
  isUploading?: boolean;
  isEditing?: boolean;
  isDeleting?: boolean;
  hasUnsavedChanges?: boolean;
}

interface ValidationErrors {
  name?: string;
  image?: string;
}

export function EventDetailsStep({ formState, onUpdate, onSubmit, onCancel, onDelete, onDuplicatePastEvent, isSubmitting, isUploading, isEditing, isDeleting, hasUnsavedChanges }: EventDetailsStepProps) {
  const [errors, setErrors] = useState<ValidationErrors>({});
  const [hasAttemptedSubmit, setHasAttemptedSubmit] = useState(false);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [selectedImageName, setSelectedImageName] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const shouldSaveDetailsOnly = Boolean(isEditing && hasUnsavedChanges);

  const validate = (): ValidationErrors => {
    const newErrors: ValidationErrors = {};

    if (!formState.name.trim()) {
      newErrors.name = 'Event name is required';
    } else if (formState.name.length > 50) {
      newErrors.name = 'Event name must be 50 characters or less';
    }

    if (!formState.eventImage && !formState.existingImageUrl) {
      newErrors.image = 'Event image is required';
    }

    return newErrors;
  };

  const getFileExtension = (file: File) => {
    const match = /\.[^.]+$/.exec(file.name);
    return match ? match[0].toLowerCase() : '';
  };

  const isHeicImageFile = (file: File) => {
    const mimeType = file.type.toLowerCase();
    return mimeType === 'image/heic' ||
      mimeType === 'image/heif' ||
      mimeType === 'image/heic-sequence' ||
      mimeType === 'image/heif-sequence' ||
      heicImageExtensions.has(getFileExtension(file));
  };

  const isAllowedEventImageFile = (file: File) => {
    const mimeType = file.type.toLowerCase();
    return allowedEventImageMimeTypes.has(mimeType) || allowedEventImageExtensions.has(getFileExtension(file));
  };

  const canPreviewImageFile = (file: File) => {
    return previewableImageMimeTypes.has(file.type.toLowerCase()) && !isHeicImageFile(file);
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (!isAllowedEventImageFile(file)) {
        onUpdate({ eventImage: null });
        setImagePreview(null);
        setSelectedImageName(null);
        setErrors(prev => ({ ...prev, image: 'Please select a JPEG, PNG, WebP, GIF, HEIC, or HEIF image.' }));
        e.target.value = '';
        return;
      }

      if (file.size > EVENT_IMAGE_MAX_BYTES) {
        onUpdate({ eventImage: null });
        setImagePreview(null);
        setSelectedImageName(null);
        setErrors(prev => ({ ...prev, image: 'Event image must be smaller than 10MB.' }));
        e.target.value = '';
        return;
      }

      onUpdate({ eventImage: file });
      setSelectedImageName(file.name);
      setErrors(({ image: _image, ...rest }) => rest);

      if (canPreviewImageFile(file)) {
        const reader = new FileReader();
        reader.onloadend = () => {
          setImagePreview(reader.result as string);
        };
        reader.readAsDataURL(file);
      } else {
        setImagePreview(null);
      }
    }
  };

  const handleRemoveImage = () => {
    onUpdate({ eventImage: null });
    setImagePreview(null);
    setSelectedImageName(null);
    setErrors(({ image: _image, ...rest }) => rest);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Re-validate when form changes after first submit attempt
  useEffect(() => {
    if (hasAttemptedSubmit) {
      setErrors(validate());
    }
  }, [formState.name, formState.eventImage, formState.existingImageUrl, hasAttemptedSubmit]);

  const handleSubmit = () => {
    setHasAttemptedSubmit(true);
    const validationErrors = validate();
    setErrors(validationErrors);

    if (Object.keys(validationErrors).length === 0) {
      onSubmit();
    }
  };

  const canSubmit = formState.name.trim().length > 0 && Object.keys(errors).length === 0;

  return (
    <div className="flex-1 flex justify-center bg-bg">
      <div className="w-full max-w-[640px] py-8 sm:py-10 lg:py-[72px] flex flex-col gap-7">
        <div>
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <h1 className="type-title-page text-text">
              Event details
            </h1>
            {onDuplicatePastEvent && (
              <button
                type="button"
                onClick={onDuplicatePastEvent}
                className="btn w-full justify-center sm:w-auto"
              >
                Duplicate Event
              </button>
            )}
          </div>
        </div>

        <div className="flex flex-col gap-2 mt-1.5">
          <div className="flex items-center justify-between">
            <label className="type-body-sm text-text">
              Event name
            </label>
            <span className={`type-caption ${formState.name.length > 50 ? 'text-warn' : 'text-text-faint'}`}>
              {formState.name.length}/50
            </span>
          </div>
          <input
            type="text"
            value={formState.name}
            onChange={(e) => onUpdate({ name: e.target.value.slice(0, 50) })}
            placeholder="Tuesday longpose"
            className={`input ${errors.name ? 'border-warn' : ''}`}
            maxLength={50}
            autoFocus
          />
          {errors.name && (
            <p className="type-validation-message" role="alert">{errors.name}</p>
          )}
        </div>

        <div className="flex flex-col gap-2 mt-4">
          <label className="type-body-sm text-text">
            Description <span className="text-text-faint font-normal">(optional)</span>
          </label>
          <textarea
            value={formState.description}
            onChange={(e) => onUpdate({ description: e.target.value })}
            placeholder="A two-hour session with long poses and warm-up sketches..."
            className="input min-h-[80px] resize-none pt-3"
            rows={3}
          />
        </div>

        <div className="flex flex-col gap-2">
          <label className="type-body-sm text-text">
            When
          </label>
          <div>
            <DateTimePicker
              value={formState.scheduledAt}
              onChange={(value) => onUpdate({ scheduledAt: value })}
              timezone={TIMEZONES.find(tz => tz.value === formState.timezone)?.label || formState.timezone}
              placeholder="Start time"
            />
          </div>
          <div className="flex flex-col gap-2 pt-2">
            <label className="type-body-sm text-text">
              Duration
            </label>
            <DurationPicker
              value={formState.duration}
              onChange={(value) => onUpdate({ duration: value })}
            />
          </div>
          <p className="type-caption text-text-faint mt-0.5">
            Times are in your local timezone ({TIMEZONES.find(tz => tz.value === formState.timezone)?.label || formState.timezone}). Rooms stay open for 1 hour after the event ends for social time.
          </p>
        </div>

        {/* Max Attendees */}
        <div className="flex flex-col gap-2">
          <label className="type-body-sm text-text">
            Max attendees <span className="text-text-faint font-normal">(optional)</span>
          </label>
          <input
            type="number"
            value={formState.maxAttendees ?? ''}
            onChange={(e) => onUpdate({ maxAttendees: e.target.value ? parseInt(e.target.value, 10) : null })}
            placeholder="No limit"
            min="1"
            className="input w-full sm:w-40"
          />
        </div>

        {/* Event Image */}
        <div className="flex flex-col gap-2">
          <label className="type-body-sm text-text">
            Event image
          </label>
          <input
            ref={fileInputRef}
            type="file"
            accept={EVENT_IMAGE_ACCEPT}
            onChange={handleImageChange}
            className="hidden"
          />
          {imagePreview ? (
            // New image selected - show preview
            <div className="relative w-full aspect-video rounded-xl overflow-hidden border border-line">
              <img src={imagePreview} alt="Event preview" className="w-full h-full object-cover" />
              <EventImageCredit
                artistCredit={formState.imageArtistCredit}
                modelCredit={formState.imageModelCredit}
              />
              <div className="type-caption absolute top-2 left-2 px-2 py-1 bg-ok/90 text-white rounded flex items-center gap-1.5">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="20 6 9 17 4 12" />
                </svg>
                Ready to upload
              </div>
              <button
                type="button"
                onClick={handleRemoveImage}
                className="absolute top-2 right-2 w-8 h-8 bg-black/50 hover:bg-black/70 rounded-lg flex items-center justify-center text-white transition-colors"
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                  <path d="M18 6L6 18M6 6l12 12" />
                </svg>
              </button>
            </div>
          ) : formState.eventImage ? (
            <div className="relative w-full aspect-video rounded-xl border border-line bg-panel2 flex flex-col items-center justify-center gap-2 px-4 text-center">
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" className="text-ok">
                <path d="M20 6L9 17l-5-5" />
              </svg>
              <span className="type-body-sm text-text">Ready to upload</span>
              <span className="type-caption text-text-dim max-w-full truncate">{selectedImageName || formState.eventImage.name}</span>
              <button
                type="button"
                onClick={handleRemoveImage}
                className="absolute top-2 right-2 w-8 h-8 bg-black/50 hover:bg-black/70 rounded-lg flex items-center justify-center text-white transition-colors"
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                  <path d="M18 6L6 18M6 6l12 12" />
                </svg>
              </button>
            </div>
          ) : formState.existingImageUrl ? (
            // Existing image - show with replace option
            <div className="relative w-full aspect-video rounded-xl overflow-hidden border border-line group cursor-pointer" onClick={() => fileInputRef.current?.click()}>
              <img src={formState.existingImageUrl} alt="Current event image" className="w-full h-full object-cover" />
              <EventImageCredit
                artistCredit={formState.imageArtistCredit}
                modelCredit={formState.imageModelCredit}
              />
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors flex items-center justify-center">
                <span className="type-body-sm text-white opacity-0 group-hover:opacity-100 transition-opacity">
                  Click to replace
                </span>
              </div>
            </div>
          ) : (
            // No image - show upload button
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="w-full aspect-video rounded-xl border-2 border-dashed border-line hover:border-text/30 flex flex-col items-center justify-center gap-2 text-text-dim hover:text-text transition-colors"
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
                <rect x="3" y="3" width="18" height="18" rx="2" />
                <circle cx="8.5" cy="8.5" r="1.5" />
                <path d="M21 15l-5-5L5 21" />
              </svg>
              <span className="type-body-sm">Click to upload an image</span>
            </button>
          )}
          {errors.image && (
            <p className="type-caption text-warn">{errors.image}</p>
          )}
          {(formState.eventImage || formState.existingImageUrl) && (
            <div className="grid grid-cols-1 gap-3 pt-2 sm:grid-cols-2">
              <div className="flex flex-col gap-1.5">
                <label className="type-caption text-text-dim" htmlFor="event-image-artist-credit">
                  Artist credit <span className="text-text-faint">(optional)</span>
                </label>
                <input
                  id="event-image-artist-credit"
                  type="text"
                  value={formState.imageArtistCredit}
                  onChange={(e) => onUpdate({ imageArtistCredit: e.target.value.slice(0, 120) })}
                  placeholder="Artist name"
                  className="input"
                  maxLength={120}
                />
              </div>
              <div className="flex flex-col gap-1.5">
                <label className="type-caption text-text-dim" htmlFor="event-image-model-credit">
                  Model credit <span className="text-text-faint">(optional)</span>
                </label>
                <input
                  id="event-image-model-credit"
                  type="text"
                  value={formState.imageModelCredit}
                  onChange={(e) => onUpdate({ imageModelCredit: e.target.value.slice(0, 120) })}
                  placeholder="Model name"
                  className="input"
                  maxLength={120}
                />
              </div>
            </div>
          )}
        </div>

        {/* Session Type */}
        <div className="flex flex-col gap-2.5 mt-2">
          <div className="type-body-sm text-text">
            Session type
          </div>

          <CheckboxOption
            checked={formState.isTutored}
            onChange={(checked) => {
              const updates: Partial<CreateFormState> = { isTutored: checked };
              if (formState.revenueSplitMode === 'manual') {
                updates.modelSplitPercent = checked ? 50 : 75;
                updates.selectedModels = formState.selectedModels.map(model => ({
                  ...model,
                  splitPercent: checked ? 50 : 75,
                }));
              }
              onUpdate(updates);
            }}
            label="This is a tutored session"
            description="Display a 'Tutored' badge to let attendees know instruction is included"
          />
        </div>

        {/* Buttons */}
        <div className="flex flex-col gap-2.5 mt-auto pt-[18px] border-t border-line-soft sm:flex-row sm:items-center">
          <button
            onClick={onCancel}
            className="btn w-full sm:w-auto"
          >
            Cancel
          </button>
          {isEditing && onDelete && (
            <button
              onClick={onDelete}
              disabled={isDeleting}
              className="btn w-full text-warn hover:bg-warn/10 sm:w-auto"
            >
              {isDeleting ? 'Deleting...' : 'Delete Event'}
            </button>
          )}
          <div className="hidden flex-1 sm:block" />
          <button
            onClick={handleSubmit}
            disabled={isSubmitting || isUploading}
            className="btn btn-accent btn-lg w-full sm:w-auto"
          >
            {isUploading ? (
              <>
                <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                Uploading image...
              </>
            ) : isSubmitting ? (
              'Saving...'
            ) : shouldSaveDetailsOnly ? (
              'Save Changes'
            ) : (
              <>Next: Model & Pricing <span>→</span></>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

// Duration picker component with hours and minutes dropdowns
function DurationPicker({ value, onChange }: { value: string; onChange: (value: string) => void }) {
  // Parse current value to get hours and minutes
  const parsed = parseDuration(value);
  const totalMinutes = parsed ?? 60; // Default to 1 hour
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;

  const handleHoursChange = (newHours: number) => {
    const newTotal = newHours * 60 + minutes;
    if (newTotal === 0) {
      onChange('0m');
    } else if (minutes === 0) {
      onChange(`${newHours}h`);
    } else {
      onChange(`${newHours}h ${minutes}m`);
    }
  };

  const handleMinutesChange = (newMinutes: number) => {
    const newTotal = hours * 60 + newMinutes;
    if (newTotal === 0) {
      onChange('0m');
    } else if (hours === 0) {
      onChange(`${newMinutes}m`);
    } else if (newMinutes === 0) {
      onChange(`${hours}h`);
    } else {
      onChange(`${hours}h ${newMinutes}m`);
    }
  };

  return (
    <div className="grid grid-cols-2 gap-2.5 sm:flex sm:items-center">
      <select
        value={hours}
        onChange={(e) => handleHoursChange(parseInt(e.target.value, 10))}
        className="input w-full py-2 px-3 sm:w-[132px]"
      >
        {[0, 1, 2, 3, 4, 5, 6].map((h) => (
          <option key={h} value={h}>
            {h} {h === 1 ? 'hour' : 'hours'}
          </option>
        ))}
      </select>
      <select
        value={minutes}
        onChange={(e) => handleMinutesChange(parseInt(e.target.value, 10))}
        className="input w-full py-2 px-3 sm:w-[148px]"
      >
        {[0, 15, 30, 45].map((m) => (
          <option key={m} value={m}>
            {m} {m === 1 ? 'minute' : 'minutes'}
          </option>
        ))}
      </select>
    </div>
  );
}

// Parse duration string like "1h 30m", "90m", "2h" into minutes
function parseDuration(duration: string): number | null {
  if (!duration.trim()) return null;

  const normalized = duration.toLowerCase().trim();
  let totalMinutes = 0;

  // Match hours
  const hoursMatch = normalized.match(/(\d+)\s*h/);
  if (hoursMatch) {
    totalMinutes += parseInt(hoursMatch[1], 10) * 60;
  }

  // Match minutes
  const minutesMatch = normalized.match(/(\d+)\s*m/);
  if (minutesMatch) {
    totalMinutes += parseInt(minutesMatch[1], 10);
  }

  // If no h or m found, try to parse as just minutes
  if (!hoursMatch && !minutesMatch) {
    const justNumber = parseInt(normalized, 10);
    if (!isNaN(justNumber)) {
      totalMinutes = justNumber;
    }
  }

  return totalMinutes > 0 ? totalMinutes : null;
}

// Export for use in other files
export { parseDuration };

function CheckboxOption({
  checked,
  onChange,
  label,
  description,
}: {
  checked: boolean;
  onChange: (checked: boolean) => void;
  label: string;
  description?: string;
}) {
  return (
    <div
      onClick={() => onChange(!checked)}
      className="flex items-start gap-3 cursor-pointer group"
    >
      <div
        className={`w-5 h-5 rounded flex items-center justify-center flex-shrink-0 border-2 transition-colors mt-0.5 ${
          checked
            ? 'border-accent bg-accent'
            : 'border-line group-hover:border-text/30'
        }`}
      >
        {checked && (
          <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
            <path d="M2.5 6L5 8.5L9.5 3.5" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        )}
      </div>
      <div>
        <div className="type-body-sm text-text">{label}</div>
        {description && (
          <div className="type-caption text-text-dim mt-0.5">{description}</div>
        )}
      </div>
    </div>
  );
}
